import { useEffect, useMemo } from 'react';
import useTripStore from '../../store/useTripStore';
import { useAutoSelectHotelRooms } from './useTripDayHotelAutoSelect';
import { useHotelAvailabilityVerdict } from './useTripDayHotelAvailability';

// US-058 prices a resolved room selection outside this hook; this one only
// decides WHICH rooms are selected (delegated to useAutoSelectHotelRooms) and
// what the day's availability verdict is once that selection settles
// (delegated to useHotelAvailabilityVerdict, read by the app through
// `registerHotelWarning`, TO-only today).
export function useTripDayHotelSelection({
  dayIndex,
  day,
  hotel,
  hotelGroup,
  hotelIsNotDistributed,
  days,
  program,
  roomsComposition,
  isFirstInGroup,
  isIncludedHotel,
  roomsData,
  roomsError,
  roomStartDate,
  roomEndDate,
  isGroupLoading,
  hasGroupError,
  isMinStayUnmet,
  dayRoomSelection,
  registerHotelWarning,
  hotelPriceOverride,
}: {
  dayIndex: number;
  day: any;
  hotel: any;
  hotelGroup: any;
  hotelIsNotDistributed: boolean;
  days: any[];
  program: any;
  roomsComposition: any[];
  isFirstInGroup: boolean;
  isIncludedHotel: any;
  roomsData: any;
  roomsError: any;
  roomStartDate: any;
  roomEndDate: any;
  isGroupLoading: boolean;
  hasGroupError: boolean;
  isMinStayUnmet: boolean;
  dayRoomSelection: any;
  registerHotelWarning?: (key: any, status: any) => void;
  // DMC only: `hotel.previousBooking`, resolved through config
  // (useHotelPriceOverride) rather than read here directly — wins over both
  // the live-priced `day.hotelPricing` and the fresh `hotel.price` fallback.
  hotelPriceOverride?: { price?: number; currency?: string };
}) {
  const updateDay = useTripStore((state) => state.updateDay);

  useAutoSelectHotelRooms({
    dayIndex,
    day,
    hotel,
    roomsComposition,
    isFirstInGroup,
    dayRoomSelection,
    roomsData,
  });

  // Mark hotel as included in day data
  useEffect(() => {
    if (hotel?.id && program?.isIncludedAccomodation !== undefined) {
      updateDay(dayIndex, {
        hotelIsIncluded: program?.isIncludedAccomodation,
      });
    }
  }, [hotel?.id, program?.isIncludedAccomodation, dayIndex, updateDay]);

  // Get the pricing info from day data (stored by updateHotelRoomSelection /
  // useTripHotelPricing)
  const groupStartDayIndex = hotelGroup?.startDayIndex;
  const hotelPricingInfo = useMemo(() => {
    if (hotelPriceOverride?.price !== undefined) {
      return hotelPriceOverride;
    }

    let result;

    if (groupStartDayIndex === undefined) {
      result =
        day?.hotelPricing ||
        (hotel?.price !== undefined
          ? { price: hotel.price, currency: hotel.currency }
          : null);
    } else {
      const firstDay = days[groupStartDayIndex];
      result = firstDay?.hotelPricing || null;
    }

    return result;
    // Also recomputed on the day and hotel identity, as before: a fresh
    // `{ price, currency }` object reaches the effects that read it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hotelPriceOverride,
    groupStartDayIndex,
    hotel?.price,
    hotel?.currency,
    hotel?.id,
    day?.hotelPricing,
    days,
    dayIndex,
    isFirstInGroup,
  ]);

  const isRoomUnavailable = useMemo(() => {
    if (!hotelGroup) return dayRoomSelection?.isUnavailable;
    const firstDay = days[hotelGroup.startDayIndex];
    return firstDay?.selectedRoom?.isUnavailable;
  }, [hotelGroup, dayRoomSelection, days]);

  // A rate discounted to nothing is still a rate, so zero is bookable: only
  // the absence of an amount is not (no pricing at all, or the NaN the
  // room-availability pass writes when the rooms that were selected have
  // gone). The price slot and the verdict below both read this, so the row
  // and the booking button can never contradict each other (KD-3643).
  const hotelPrice = hotelPricingInfo?.price;
  const hasBookablePrice = hotelPrice != null && !isNaN(hotelPrice);

  useHotelAvailabilityVerdict({
    dayIndex,
    hotel,
    hotelIsNotDistributed,
    hotelGroup,
    isFirstInGroup,
    isIncludedHotel,
    isGroupLoading,
    hasGroupError,
    isRoomUnavailable,
    isMinStayUnmet,
    hasBookablePrice,
    roomStartDate,
    roomEndDate,
    roomsData,
    roomsError,
    registerHotelWarning,
  });

  return { hotelPricingInfo, isRoomUnavailable, hasBookablePrice };
}
